// Slice Notation Parser for infragraph
// Handles Python-like slice notation: "component[0:4]", "nic[5]", "port[:8]"

import { SliceResult } from '../types/infragraph';

export class SliceNotationParser {
  /**
   * Parse slice notation and return structured result
   *
   * Supported formats:
   * - "component[5]" → single index [5]
   * - "component[0:4]" → range [0, 1, 2, 3]
   * - "component[:8]" → open start [0...7]
   * - "component[2:]" → open end [2...maxCount-1] (requires maxCount)
   * - "component" → all indices [0...maxCount-1] (requires maxCount)
   *
   * @param notation - The slice notation string
   * @param maxCount - Maximum count for open ranges or no-slice cases
   * @returns SliceResult with parsed information
   */
  parse(notation: string, maxCount?: number): SliceResult {
    // Check if notation contains brackets
    const bracketMatch = notation.match(/^([^[]+)\[([^\]]*)\]$/);

    if (!bracketMatch) {
      // No brackets - if maxCount provided, return range 0 to maxCount-1
      // Otherwise, treat as a simple named reference (not slice notation)
      if (maxCount === undefined) {
        // Simple name reference without slice notation
        return {
          name: notation,
          start: 0,
          end: 1,
          isSingle: true,
          hasSlice: false
        };
      }

      return {
        name: notation,
        start: 0,
        end: maxCount,
        isSingle: false,
        hasSlice: false
      };
    }

    const name = bracketMatch[1];
    const sliceContent = bracketMatch[2];

    // Check for range notation (contains ':')
    if (sliceContent.includes(':')) {
      const parts = sliceContent.split(':');
      if (parts.length !== 2) {
        throw new Error(`Invalid slice notation: ${notation}. Expected format: name[start:end]`);
      }

      const startStr = parts[0].trim();
      const endStr = parts[1].trim();

      let start = 0;
      let end = maxCount || 0;

      // Parse start
      if (startStr !== '') {
        start = parseInt(startStr, 10);
        if (isNaN(start)) {
          throw new Error(`Invalid start index in slice notation: ${notation}`);
        }
      }

      // Parse end
      if (endStr !== '') {
        end = parseInt(endStr, 10);
        if (isNaN(end)) {
          throw new Error(`Invalid end index in slice notation: ${notation}`);
        }
      } else if (maxCount === undefined) {
        throw new Error(`Open-ended slice notation requires maxCount: ${notation}`);
      }

      // Validate range
      if (start < 0 || end < 0) {
        throw new Error(`Negative indices not supported in slice notation: ${notation}`);
      }
      if (start >= end) {
        throw new Error(`Invalid range in slice notation (start >= end): ${notation}`);
      }

      return {
        name,
        start,
        end,
        isSingle: false,
        hasSlice: true
      };
    } else {
      // Single index notation
      const index = parseInt(sliceContent, 10);
      if (isNaN(index)) {
        throw new Error(`Invalid index in slice notation: ${notation}`);
      }
      if (index < 0) {
        throw new Error(`Negative index not supported in slice notation: ${notation}`);
      }

      return {
        name,
        start: index,
        end: index + 1,
        isSingle: true,
        hasSlice: true
      };
    }
  }

  /**
   * Expand slice notation to array of indices
   *
   * @param slice - Parsed slice result
   * @returns Array of indices
   */
  expandToIndices(slice: SliceResult): number[] {
    const indices: number[] = [];
    for (let i = slice.start; i < slice.end; i++) {
      indices.push(i);
    }
    return indices;
  }

  /**
   * Expand slice notation to fully qualified names
   *
   * @param notation - The slice notation string
   * @param maxCount - Maximum count for open ranges
   * @returns Array of fully qualified names
   *
   * @example
   * expandToNames("server[0:2]") → ["server.0", "server.1"]
   * expandToNames("nic[5]") → ["nic.5"]
   * expandToNames("port[:3]") → ["port.0", "port.1", "port.2"]
   */
  expandToNames(notation: string, maxCount?: number): string[] {
    const slice = this.parse(notation, maxCount);

    // If it's a simple reference without brackets or slice syntax, return as-is
    if (slice.isSingle && !slice.hasSlice) {
      return [slice.name];
    }

    const indices = this.expandToIndices(slice);
    return indices.map(index => `${slice.name}.${index}`);
  }

  /**
   * Check if a notation string contains slice syntax
   *
   * @param notation - The notation string to check
   * @returns true if contains brackets, false otherwise
   */
  hasSliceSyntax(notation: string): boolean {
    return notation.includes('[') && notation.includes(']');
  }

  /**
   * Extract the base name from a slice notation
   *
   * @param notation - The slice notation string
   * @returns The base name without brackets
   *
   * @example
   * getBaseName("component[0:4]") → "component"
   * getBaseName("nic") → "nic"
   */
  getBaseName(notation: string): string {
    const bracketIndex = notation.indexOf('[');
    if (bracketIndex === -1) {
      return notation;
    }
    return notation.substring(0, bracketIndex);
  }

  /**
   * Validate slice notation without parsing
   *
   * @param notation - The slice notation string
   * @returns true if valid, false otherwise
   */
  isValid(notation: string): boolean {
    try {
      // Try parsing with a reasonable maxCount for validation
      this.parse(notation, 1000);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Parse multiple slice notations and return their expanded indices
   * Useful for edge endpoint expansion
   *
   * @param notations - Array of slice notation strings
   * @param maxCount - Maximum count for each notation
   * @returns Array of arrays of indices
   */
  parseMultiple(notations: string[], maxCount?: number): number[][] {
    return notations.map(notation => {
      const slice = this.parse(notation, maxCount);
      return this.expandToIndices(slice);
    });
  }
}

// Export singleton instance for convenience
export const sliceParser = new SliceNotationParser();

// Export helper functions for direct use
export function parseSlice(notation: string, maxCount?: number): SliceResult {
  return sliceParser.parse(notation, maxCount);
}

export function expandSliceToIndices(notation: string, maxCount?: number): number[] {
  const slice = sliceParser.parse(notation, maxCount);
  return sliceParser.expandToIndices(slice);
}

export function expandSliceToNames(notation: string, maxCount?: number): string[] {
  return sliceParser.expandToNames(notation, maxCount);
}

export function getSliceBaseName(notation: string): string {
  return sliceParser.getBaseName(notation);
}
